import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  Avatar,
  Link,
  LinkButton,
  MoreOptions,
  ProgressBar,
  RichTextValue
} from 'components/common';
import { Icon } from 'components/common/icons';
import { ReviewStars } from 'components/common/tasks';
import {
  CalendarEventFeedbackQuestionRemove,
  CalendarEventFeedbackQuestionSave
} from '.';

import type { CalendarEventFeedbackQuestion } from 'shared-types';
import type { GetCalendarEventFeedbackAnalyticsRes } from 'shared-types/responses';

interface Props {
  analytics?: GetCalendarEventFeedbackAnalyticsRes['analyticsByQuestionId'][number];
  getQuestionHref?: (question: CalendarEventFeedbackQuestion) => string;
  hasSubmissions: boolean;
  isDefaultTemplate?: boolean;
  isTemplate?: boolean;
  number: number;
  question: CalendarEventFeedbackQuestion;
  questions: CalendarEventFeedbackQuestion[];
  questionsUrl: string;
  updateQuestions: () => Promise<void>;
}

/**
 * A compact analytics block for one question in the questions table: how many
 * submissions answered it, plus the average rating for star ratings or every
 * option with its share of the answers for choice questions. For multiple
 * choice the shares can add up to more than 100%, same as on the question's
 * own page.
 */
const CalendarEventFeedbackQuestionSummary = ({
  analytics,
  getQuestionHref,
  hasSubmissions,
  isDefaultTemplate,
  isTemplate,
  number,
  question,
  questions,
  questionsUrl,
  updateQuestions
}: Props) => {
  const { messages, theme } = useReduxState();

  const questionTypeLabels = {
    multiplechoice: messages.eventCalendarFeedbackTypeMultipleChoice,
    openquestion: messages.eventCalendarFeedbackTypeOpenQuestion,
    singlechoice: messages.eventCalendarFeedbackTypeSingleChoice,
    starrating: messages.eventCalendarFeedbackTypeStarRating
  };

  const isOnlyQuestion = questions.length === 1;

  const removeQuestionDisabled =
    hasSubmissions || (isDefaultTemplate && isOnlyQuestion);

  const removeQuestionTooltip = hasSubmissions
    ? messages.eventCalendarFeedbackQuestionsLockedTooltip
    : isDefaultTemplate && isOnlyQuestion
    ? messages.siteSettingsCalendarEventFeedbackQuestionsDefaultTemplateTooltip
    : undefined;

  const getShareOfAnswers = (numberOfAnswers: number) =>
    analytics?.numberOfAnswers
      ? numberOfAnswers / analytics.numberOfAnswers
      : 0;

  return (
    <div className="summary">
      <div className="header">
        <div className="header-number">
          <Avatar>{number}</Avatar>
        </div>

        <div className="header-meta">
          {questionTypeLabels[question.type]}

          {question.isRequired ? null : (
            <>
              <span style={{ padding: '0 10px' }}>{'•'}</span>
              <span>{messages.commonOptional}</span>
            </>
          )}
        </div>

        <div className="header-actions">
          <MoreOptions renderAsFixed={true} styleContentButtons={false}>
            <CalendarEventFeedbackQuestionSave
              disabled={hasSubmissions}
              isInsideDropdown={true}
              question={question}
              questionsUrl={questionsUrl}
              updateQuestions={updateQuestions}
            />

            <CalendarEventFeedbackQuestionRemove
              disabled={removeQuestionDisabled}
              questionId={question.id}
              questionsUrl={questionsUrl}
              tooltip={removeQuestionTooltip}
              updateQuestions={updateQuestions}
            />
          </MoreOptions>
        </div>
      </div>

      <div className="content">
        <div className="question">
          {getQuestionHref ? (
            <Link href={getQuestionHref(question)} style={{ fontWeight: 400 }}>
              <RichTextValue html={question.question} />
            </Link>
          ) : (
            <RichTextValue html={question.question} />
          )}
        </div>

        <div className="analytics">
          {!isTemplate && question.type === 'starrating' ? (
            <div className="analytics-rating">
              <div className="analytics-rating-label">
                {messages.eventCalendarFeedbackAverageRating}
                {':'}
              </div>

              <div className="analytics-rating-value">
                {analytics?.type === 'starrating' && analytics.averageRating
                  ? analytics.averageRating
                  : '-'}
              </div>

              {analytics?.type === 'starrating' && analytics.averageRating ? (
                <div className="analytics-rating-stars">
                  <ReviewStars
                    numberOfStars={question.data.numberOfStars}
                    value={analytics.averageRating}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {question.type === 'singlechoice' ||
          question.type === 'multiplechoice' ? (
            <ul className="analytics-options">
              {question.data.options.map((option) => {
                const numberOfAnswers =
                  analytics?.type === 'multiplechoice' ||
                  analytics?.type === 'singlechoice'
                    ? analytics?.options.find(
                        (item) => item.optionId === option.id
                      )?.numberOfAnswers || 0
                    : 0;
                const shareOfAnswers = getShareOfAnswers(numberOfAnswers);

                return (
                  <li className="analytics-option" key={option.id}>
                    {/* cut to one line to keep the table row short. the full
                    text stays in the DOM for screen readers, shows on
                    hover, and is on the question's own page */}
                    <span className="analytics-option-text" title={option.text}>
                      {option.text}
                    </span>

                    {!isTemplate ? (
                      <span className="analytics-option-progress">
                        <span className="analytics-option-progress-bar">
                          <ProgressBar
                            color={theme.chartMainColor}
                            progress={shareOfAnswers}
                            progressStyle={{ height: 6 }}
                          />
                        </span>

                        <span className="analytics-option-value">
                          {`${Math.round(
                            shareOfAnswers * 100
                          )}% (${numberOfAnswers})`}
                        </span>
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {getQuestionHref ? (
            <div className="analytics-footer">
              <div className="analytics-footer-action">
                <LinkButton
                  category="lighter"
                  href={getQuestionHref(question)}
                  isRounded={true}
                  size="small"
                  text={
                    <div
                      style={{ alignItems: 'center', display: 'flex', gap: 5 }}
                    >
                      {getMessageWithPlaceholders({
                        message: messages.commonNumberOfAnswers,
                        placeholders: {
                          numberOfAnswers: analytics?.numberOfAnswers || 0
                        }
                      })}

                      <Icon
                        icon="chevron-right"
                        style={{
                          fontSize: '1rem',
                          position: 'relative',
                          top: 1
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .header {
          align-items: center;
          display: flex;
          gap: 15px;
        }
        .header-number {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .header-meta {
          color: ${theme.textColorLighter};
          flex-grow: 1;
          flex-shrink: 1;
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 1px;
        }
        .header-actions {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .content {
          margin-top: 15px;
        }
        .question {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin-bottom: 15px;
        }
        .analytics {
          color: ${theme.textColor};
        }
        .analytics-rating {
          align-items: center;
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.buttonBorderRadius};
          display: flex;
          gap: 10px;
          margin: 20px 0 10px;
          padding: 5px 10px;
        }
        .analytics-rating-label {
          color: ${theme.textColorLight};
          flex-grow: 1;
          flex-shrink: 1;
          font-size: 1.4rem;
          font-weight: 600;
        }
        .analytics-rating-value {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .analytics-rating-stars {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          position: relative;
          top: 1px;
        }
        .analytics-options {
          list-style: none;
          margin: 25px 0 10px;
          padding: 0;
        }
        .analytics-option {
          align-items: flex-start;
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.buttonBorderRadius};
          display: flex;
          flex-direction: column;
          font-size: 1.4rem;
          gap: 10px 20px;
          grid-template-columns: minmax(0, 1fr) 120px 70px;
          padding: 5px 10px;
        }
        .analytics-option:not(:first-child) {
          margin-top: 10px;
        }
        .analytics-option-text {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .analytics-option-progress {
          align-items: center;
          display: flex;
          gap: 20px;
        }
        .analytics-option-progress-bar {
          flex-grow: 0;
          flex-shrink: 0;
          width: 100px;
        }
        .analytics-option-value {
          flex-grow: 0;
          flex-shrink: 0;
          font-weight: 600;
          text-align: right;
          white-space: nowrap;
          width: 60px;
        }
        .analytics-footer {
          align-items: center;
          display: flex;
          gap: 20px;
          justify-content: flex-end;
          padding: 15px 5px 10px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .analytics-option {
            align-items: center;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackQuestionSummary;
